import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon, VerticalCustom } from '@repo/go-icons';
import { ActionsBar, Button, Drawer, Tooltip } from '@repo/go-ui';
import { TrajectoryWidget as TrajectoryWidgetComponent } from '@repo/go-widgets/trajectory';
import {
  DEFAULT_REFERENCE_WELLS_VISIBILITY,
  parseVersionedReferenceWellsSettings,
  ReferenceDataTemplatesStore,
  ReferenceWellsChildrenStore,
  ReferenceWellsPanel,
  ReferenceWellsService,
  ReferenceWellsStore,
} from '@repo/reference-wells';
import { hasValue } from '@repo/utils';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from 'src/app/i18n';
import { requireService, useService } from 'src/packages/di';
import { ReferenceDataTemplatesApi } from 'src/packages/reference-data-templates-service/reference-data-templates-api';
import { ReferenceGroupFavoritesAdapter } from 'src/packages/reference-wells-favorites/reference-group-favorites-adapter';
import { createEnrichedReferenceWellsFullInfoApi } from 'src/packages/reference-wells-full-info-service/create-enriched-reference-wells-full-info-api';
import { ReferenceWellsFullInfoApi } from 'src/packages/reference-wells-full-info-service/reference-wells-full-info-api';
import { createReferenceWellsFiltersHostBridge } from 'src/packages/reference-wells-persistence/create-reference-wells-filters-host-bridge';
import { takePendingReferenceWellsTree } from 'src/packages/reference-wells-persistence/reference-wells-pending-tree-registry';

import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import { TrajectorySelectionHeader } from './TrajectorySelectionHeader';
import type { TrajectoryWidgetEntity } from './TrajectoryWidget.entity';
import styles from './TrajectoryWidget.module.scss';
import { TrajectoryWidgetStore } from './TrajectoryWidget.store';

type WrapperProps = {
  entity: TrajectoryWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: TrajectoryWidgetStore;
  entity: TrajectoryWidgetEntity;
  options: TWidgetRendererOptions;
};

const TRAJECTORY_REFERENCE_WELLS_VISIBILITY = {
  ...DEFAULT_REFERENCE_WELLS_VISIBILITY,
};

export const TrajectoryWidgetWrapper = observer(function TrajectoryWidgetWrapper({ entity, options }: WrapperProps) {
  const [store, setStore] = useState<TrajectoryWidgetStore | null>(null);
  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const existing = storeService.getStore<TrajectoryWidgetEntity, TrajectoryWidgetStore>(entity);

    if (existing) {
      existing.referenceWellsStore.setFiltersHostBridge(
        createReferenceWellsFiltersHostBridge({
          getGroupId: () => entity.groupId,
          groupSelectStore: options.groupSelectStore,
        }),
      );
      setStore(existing);
    } else {
      const pendingTree = takePendingReferenceWellsTree(entity.uuid);
      const initialSettings = pendingTree !== undefined ? parseVersionedReferenceWellsSettings(pendingTree) : undefined;

      const referenceWellsService = new ReferenceWellsService({
        wellsSource: requireService('wellsData'),
        primaryWellId: entity.wellId,
        initialSelectedTree: initialSettings?.tree,
        visibility: TRAJECTORY_REFERENCE_WELLS_VISIBILITY,
      });
      const referenceWellsStore = new ReferenceWellsStore(referenceWellsService, {
        initialFilters: initialSettings?.filters ?? null,
        persistedSettingsToRestore: entity.restoredFromTabSnapshot ? initialSettings : undefined,
        filtersHostBridge: createReferenceWellsFiltersHostBridge({
          getGroupId: () => entity.groupId,
          groupSelectStore: options.groupSelectStore,
        }),
      });
      const referenceDataTemplatesStore = new ReferenceDataTemplatesStore({
        api: new ReferenceDataTemplatesApi(),
        wellsService: referenceWellsService,
        getOwnerUserId: () => requireService('authService').userInfo.sub,
        getDraftTree: () => referenceWellsStore.selectedTree,
        getDraftFilters: () => referenceWellsStore.filters,
        applyReferenceWellsSettings: (settings) => referenceWellsStore.applySettingsFromGroup(settings),
        onCommittedSelection: () => referenceWellsStore.syncDraftFromApplied(),
        groupFavorites: new ReferenceGroupFavoritesAdapter(requireService('userPreference')),
      });
      const referenceWellsChildrenStore = new ReferenceWellsChildrenStore({
        api: createEnrichedReferenceWellsFullInfoApi(new ReferenceWellsFullInfoApi()),
        referenceWellsStore,
      });

      const newStore = new TrajectoryWidgetStore(entity, options.groupSelectStore, options.wellListEntity, {
        referenceWellsService,
        referenceWellsStore,
        referenceDataTemplatesStore,
        referenceWellsChildrenStore,
      });
      storeService.setStore<TrajectoryWidgetEntity, TrajectoryWidgetStore>(entity, newStore);
      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return <TrajectoryWidgetView store={store} entity={entity} options={options} />;
});

export const TrajectoryWidgetView = observer(function TrajectoryWidgetView({ entity, options, store }: Props) {
  const { t } = useTranslation(['common', 'widgets']);
  const notifications = useService('notifications');
  const language = useService('language');
  const directories = useService('directories');
  const userPreference = useService('userPreference');
  const [widgetRoot, setWidgetRoot] = useState<HTMLDivElement | null>(null);

  const trajectoryPreferences = useMemo(
    () => ({
      getDefaultAzimuthReference: () => userPreference.preferences.azimuthReference ?? 'TRUE',
    }),
    [userPreference],
  );

  const { isFullscreen, setFullscreen, isInteractionBlocked } = entity;
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === entity.wellId)?.data.name ?? entity.wellId;
  const primaryWellName = hasValue(wellName) ? String(wellName) : null;

  useEffect(() => store.effect(), [store, store.effect]);

  return (
    <div className={styles.trajectoryWidgetWrapper}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={store.setGroupForTrajectory}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => (
          <>
            {!isFullscreen && (
              <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                <Button
                  onClick={() => setFullscreen(true)}
                  variant="flat"
                  icon={<FullIcon className={styles.icon} />}
                  className={styles.button}
                />
              </Tooltip>
            )}
            {isFullscreen && (
              <Tooltip title={t('common:Widget.collapse')} delay={400}>
                <Button
                  onClick={() => setFullscreen(false)}
                  variant="flat"
                  icon={<MinimizeIcon className={styles.icon} />}
                  className={styles.button}
                />
              </Tooltip>
            )}
          </>
        )}
      />

      <div className={styles.wrapper}>
        <div ref={setWidgetRoot} className={styles.trajectoryRoot}>
          {isInteractionBlocked && <div className={styles.interactionOverlay} />}
          <TrajectoryWidgetComponent
            trajectoryService={store.trajectoryService}
            notifications={notifications}
            language={language.language}
            parentI18n={i18n}
            initialState={entity.stateParams}
            onStateChange={store.setStateParams}
            showTableToolbar
            isReferenceDataLoading={store.isReferenceDataLoading}
            wellNamesByWellboreId={store.wellNamesByWellboreId}
            mainHeader={<TrajectorySelectionHeader store={store} />}
            extraActionsBarItems={
              <Tooltip placement="left" title={t('widgets:Trajectory.referenceWells')} delay={400}>
                <ActionsBar.Item active={store.isReferenceWellsPanelOpen} onClick={store.toggleReferenceWellsPanel}>
                  <VerticalCustom />
                </ActionsBar.Item>
              </Tooltip>
            }
            directories={directories}
            preferences={trajectoryPreferences}
          />
          <Drawer
            title={t('widgets:Trajectory.referenceWells')}
            open={store.isReferenceWellsPanelOpen}
            onClose={() => store.setReferenceWellsPanelOpen(false)}
            container={widgetRoot ?? undefined}
          >
            <ReferenceWellsPanel
              store={store.referenceWellsStore}
              templatesStore={store.referenceDataTemplatesStore}
              language={language.language}
              parentI18n={i18n}
              primaryWellName={primaryWellName}
              showHeader={false}
            />
          </Drawer>
        </div>
      </div>
    </div>
  );
});
